import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Camera, RefreshCw } from 'lucide-react';

export interface CameraViewfinderRef {
  getVideo: () => HTMLVideoElement | null;
  getStream: () => MediaStream | null;
  toggleTorch: () => Promise<void>;
  captureFrame: (quality?: number) => Promise<File | null>;
}

export interface CameraViewfinderProps {
  onReady?: (info: {
    video: HTMLVideoElement;
    stream: MediaStream;
    torchAvailable: boolean;
  }) => void;
  onError?: (err: string) => void;
  className?: string;
  aspectRatioClass?: string;
  minHeightClass?: string;
  children?: React.ReactNode;
}

export const CameraViewfinder = React.forwardRef<CameraViewfinderRef, CameraViewfinderProps>(
  (
    {
      onReady,
      onError,
      className = '',
      aspectRatioClass = 'aspect-[3/4] sm:aspect-[4/3]',
      minHeightClass = 'min-h-[360px] sm:min-h-[460px]',
      children,
    },
    ref
  ) => {
    const videoRef = useRef<HTMLVideoElement | null>(null);
    const streamRef = useRef<MediaStream | null>(null);
    const [isReady, setIsReady] = useState(false);
    const [cameraError, setCameraError] = useState<string | null>(null);
    const [torchAvailable, setTorchAvailable] = useState(false);
    const [isTorchOn, setIsTorchOn] = useState(false);
    const [retryCount, setRetryCount] = useState(0);

    const onReadyRef = useRef(onReady);
    onReadyRef.current = onReady;
    const onErrorRef = useRef(onError);
    onErrorRef.current = onError;

    const stopStream = useCallback(() => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => {
          try {
            track.stop();
          } catch {}
        });
        streamRef.current = null;
      }
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
      setIsReady(false);
      setIsTorchOn(false);
    }, []);

    const toggleTorch = useCallback(async () => {
      if (!streamRef.current) return;
      const track = streamRef.current.getVideoTracks()[0];
      if (!track) return;

      try {
        const nextState = !isTorchOn;
        await (track as any).applyConstraints({
          advanced: [{ torch: nextState }],
        });
        setIsTorchOn(nextState);
      } catch (e) {
        console.warn('Torch toggle error:', e);
      }
    }, [isTorchOn]);

    const captureFrame = useCallback(
      async (quality: number = 0.85): Promise<File | null> => {
        const video = videoRef.current;
        if (!video || !isReady) return null;

        const width = video.videoWidth || 1280;
        const height = video.videoHeight || 720;
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        if (!ctx) return null;

        ctx.drawImage(video, 0, 0, width, height);

        return new Promise<File | null>((resolve) => {
          canvas.toBlob(
            (blob) => {
              if (!blob) {
                resolve(null);
                return;
              }
              const file = new File([blob], `camera-snap-${Date.now()}.jpg`, {
                type: 'image/jpeg',
              });
              resolve(file);
            },
            'image/jpeg',
            quality
          );
        });
      },
      [isReady]
    );

    React.useImperativeHandle(
      ref,
      () => ({
        getVideo: () => videoRef.current,
        getStream: () => streamRef.current,
        toggleTorch,
        captureFrame,
      }),
      [toggleTorch, captureFrame]
    );

    useEffect(() => {
      let isMounted = true;
      setCameraError(null);
      setIsReady(false);

      async function initCamera() {
        stopStream();

        try {
          // Standard environment camera constraints optimal for mobile iOS WebKit
          const stream = await navigator.mediaDevices.getUserMedia({
            video: {
              facingMode: { ideal: 'environment' },
            },
            audio: false,
          });

          if (!isMounted) {
            stream.getTracks().forEach((t) => t.stop());
            return;
          }

          streamRef.current = stream;

          const videoTrack = stream.getVideoTracks()[0];
          let hasTorch = false;
          if (videoTrack) {
            const capabilities: any = videoTrack.getCapabilities ? videoTrack.getCapabilities() : {};
            hasTorch = Boolean(capabilities && capabilities.torch);
            setTorchAvailable(hasTorch);
          }

          const video = videoRef.current;
          if (video) {
            video.srcObject = stream;
            video.muted = true;
            video.playsInline = true;
            video.setAttribute('playsinline', 'true');
            video.setAttribute('webkit-playsinline', 'true');

            // Force play inline
            try {
              await video.play();
            } catch (playErr) {
              console.warn('Initial video play error, waiting on metadata:', playErr);
            }

            const handleReady = () => {
              if (!isMounted) return;
              setIsReady(true);
              onReadyRef.current?.({
                video,
                stream,
                torchAvailable: hasTorch,
              });
            };

            if (video.readyState >= 2 && video.videoWidth > 0) {
              handleReady();
            } else {
              video.onloadeddata = handleReady;
              video.oncanplay = handleReady;
            }
          }
        } catch (err: any) {
          if (!isMounted) return;
          console.warn('CameraViewfinder getUserMedia error:', err);
          const msg =
            err?.name === 'NotAllowedError'
              ? 'Camera permission denied. Please allow camera access in Settings.'
              : 'Could not connect to camera hardware. Please tap Retry.';
          setCameraError(msg);
          onErrorRef.current?.(msg);
        }
      }

      initCamera();

      return () => {
        isMounted = false;
        stopStream();
      };
    }, [retryCount, stopStream]);

    return (
      <div
        className={`relative w-full ${aspectRatioClass} ${minHeightClass} rounded-3xl bg-black overflow-hidden border border-zinc-800 shadow-2xl flex items-center justify-center ${className}`}
        style={{
          WebkitTransform: 'translateZ(0)',
          transform: 'translateZ(0)',
          WebkitMaskImage: '-webkit-radial-gradient(white, black)',
        }}
      >
        {/* Hardware-accelerated Video Element */}
        <video
          ref={videoRef}
          playsInline
          muted
          autoPlay
          className="absolute inset-0 w-full h-full object-cover"
          style={{
            WebkitTransform: 'translateZ(0)',
            transform: 'translateZ(0)',
          }}
        />

        {/* Loading Spinner State */}
        {!isReady && !cameraError && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/75 z-10 space-y-3">
            <div className="w-10 h-10 rounded-full border-2 border-emerald-400 border-t-transparent animate-spin" />
            <p className="text-xs text-zinc-300 font-medium tracking-wide">
              Starting camera...
            </p>
          </div>
        )}

        {/* Camera Error State with Retry Button */}
        {cameraError && (
          <div className="absolute inset-0 flex flex-col items-center justify-center p-6 bg-black/85 z-20 space-y-3 text-center">
            <Camera className="w-10 h-10 text-rose-400 mx-auto" />
            <p className="text-xs text-rose-300 bg-rose-950/50 p-2.5 rounded-xl border border-rose-800/60 leading-relaxed max-w-xs font-medium">
              {cameraError}
            </p>
            <button
              type="button"
              onClick={() => setRetryCount((c) => c + 1)}
              className="h-10 px-4 rounded-xl bg-white text-zinc-950 font-bold text-xs flex items-center gap-2 shadow-lg active:scale-95 transition-all"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              <span>Retry Camera</span>
            </button>
          </div>
        )}

        {/* Overlays / Controls mounted directly on top of the live video */}
        {isReady && children}
      </div>
    );
  }
);
